// ── Single source of truth for all company-related options ──────
// Used by both CompanyExplore (explorer filters) and CompanyManage (recruiter form)

// Cevee Tags - predefined, curated branding vibes
// Recruiters pick from this pool. Explorers filter by them.
export const CEVEE_TAGS_POOL = [
  // Identity & Vibes
  'Klein aber fein', 'Chaos aber nice', 'Old but gold', 'Move fast',
  'Hidden champion', 'Family vibes', 'Design-driven', 'Impact-first',
  'Voll remote', 'Grown-up Startup', 'Underdog energy', 'Corporate mit Herz',
  'Gründer:innen-DNA', 'Chill but ambitious', 'Culture > Perks',
  'Purpose-driven', 'Hands-on Mentalität', 'Teamplayer-Kultur',
  'Tradition trifft Innovation', 'Nahbar & echt', 'Wir statt Ich',
  'Macher:innen-DNA', 'Lokal verwurzelt', 'Built different',
  'Nerd paradise', 'Rocket ship 🚀', 'Bootstrapped', 'Deep Tech',
  'No Bullshit', 'Real Talk Kultur', 'Lernort statt Karriereleiter',
  'Ausbildung mit Herz', 'Meister:innen gesucht',
  // Startup & Growth
  'VC-backed', 'Pre-Seed', 'Seed Stage',
  'Series A', 'Hyper Growth', 'Global ambition', '0 to 1',
  'Product-led', 'Founding team', 'Equity for all',
  'Fail fast, learn faster', 'Ship it', 'Day 1 mentality',
  'Builder culture', 'Ownership', 'Wear many hats',
  'Serial entrepreneur', 'Experienced team', 'First 10 hires',
  'Exit-erfahren',
  // Culture & Work Style
  'Remote-first', 'Flat Hierarchy', 'Diverse & Inclusive',
  'Work-Life Balance', '4-Tage-Woche', 'Dog-friendly',
  'Learning Culture', 'Team Events', 'Flexible Hours', 'Teilzeit möglich',
  'Mentorship', 'Nachhaltigkeit', 'Creative Freedom', 'International',
  'Mental Health matters', 'Ausbildung willkommen',
  'Quereinstieg möglich', 'Duzen statt Siezen',
  'Async-first', 'No Meetings Day', 'Workation', 'Home Office Budget',
  'Offsites', 'Unlimited PTO', 'Sabbatical möglich', 'Bomb Benefits',
  'Transparente Gehälter', 'Open Door Policy',
  // Non-Profit & Social / Gen Z DE
  'Non-Profit', 'Social Impact', 'Ehrenamt meets Job', 'Gemeinnützig',
  'Klima-First', 'Female founded', 'Migrant-friendly',
  'Werkstudent:innen welcome', 'Side Hustle ok',
  'Safe Space', 'LGBTQ+ friendly', 'Barrierefrei',
  // Industry-specific vibes
  'B2B SaaS', 'B2C', 'Developer Tools', 'FinTech', 'HealthTech',
  'EdTech', 'CleanTech', 'PropTech', 'FoodTech', 'LegalTech',
  'Mittelstand 2.0', 'Familienunternehmen', 'Agenturleben',
  'Klinik & Praxis', 'Handwerk mit Zukunft',
];

/** @deprecated Use CEVEE_TAGS_POOL instead */
export const CULTURE_SUGGESTIONS = CEVEE_TAGS_POOL;

// Industries - curated against actual company data (prod audit Apr 2026).
// Removed: 30+ nudges that had 0 companies (Pharma, Chemie, Banking,
// Versicherung, Maschinenbau, Luftfahrt, NGO, Fitness, Bildung, etc.).
// Added: categories that appear in real DB (Manufacturing, FinTech,
// HealthTech, CleanTech, Robotics, Biotech, Fashion, Energy, Accounting).
export const INDUSTRY_SUGGESTIONS = [
  // Tech
  'Software', 'AI', 'IT', 'SaaS', 'FinTech', 'HealthTech', 'CleanTech',
  'FoodTech', 'Biotech', 'Robotics', 'Gaming', 'Entertainment',
  // Business & Services
  'Startup', 'Consulting', 'Agentur', 'Marketing',
  // Commerce & Lifestyle
  'E-Commerce', 'Mode', 'Fashion', 'Beauty',
  // Core sectors
  'Manufacturing', 'Industrie', 'Handwerk', 'Energy',
  'Food & Beverage', 'Lebensmittel', 'Gastronomie',
  'Gesundheit', 'Finanzen', 'Accounting', 'Immobilien',
  'Logistik', 'Sport', 'Non-Profit',
];

// Locations - curated against actual company data (prod audit Apr 2026).
// Removed: Bundesländer, Wirtschaftsregionen, Lifestyle labels
// (Pendlerfreundlich, Bodensee, Alpenregion, etc.) that had 0 companies.
// Added: international hubs that actually appear in DB
// (London, San Francisco, New York, Amsterdam, Paris, Barcelona, Lausanne).
// English/German city duplicates (Munich/München, Cologne/Köln) handled
// via i18n labels + semantic search, no duplicate nudge needed.
export const LOCATION_SUGGESTIONS = [
  // Top Städte DE
  'Berlin', 'München', 'Hamburg', 'Köln', 'Frankfurt', 'Stuttgart',
  'Düsseldorf', 'Leipzig', 'Freiburg', 'Nürnberg', 'Hannover',
  // CH / AT
  'Zürich', 'Wien', 'Lausanne',
  // International hubs (appear in real company data)
  'London', 'San Francisco', 'New York', 'Amsterdam', 'Paris', 'Barcelona',
  // Work style & macro
  'Remote', 'Hybrid', 'DACH', 'Europa',
  'Internationale Community', 'Work from Anywhere',
  // Vibe locations - semantic nudges that match via Gemini search.
  // E.g. "Am See" → Zürich/Lausanne, "Berge in Reichweite" → München/Zürich.
  'Am See', 'Am Meer', 'Am Fluss',
  'Berge in Reichweite', 'Sonniger Süden',
  'Hauptstadt-Feeling', 'Hafenstadt', 'Studi-Stadt', 'Metropole',
  'Tech Hub', 'Startup Hub',
];

// Roles - curated against actual company data (prod audit Apr 2026).
// Removed: ~45 nudges that had 0 postings (Bauingenieur, KFZ-Mechatroniker,
// Dachdecker, MFA, Apotheker, Immobilienmakler, LKW-Fahrer, Azubis, etc.).
// Kept despite 0× by request: AI Engineering Intern, Jura-Rollen
// (Jurist/Rechtsanwalt/Notar/Legal Counsel), Kreativ-Rollen (Content Creator,
// Redakteur, Copywriter), Psycholog:in.
// Added: high-frequency DB tokens (Senior, Lead, Head, Working Student,
// Talent Acquisition, Internship, Junior, Director, Brand, Influencer).
export const LOOKING_FOR_SUGGESTIONS = [
  // Engineering (core)
  'Founding Engineer', 'Software Engineer', 'Senior Engineer', 'Tech Lead',
  'Frontend Engineer', 'Backend Engineer', 'Fullstack Engineer',
  'DevOps Engineer', 'Platform Engineer', 'Solutions Engineer',
  'Forward Deployed Engineer', 'Research Engineer',
  'Software Engineering Intern', 'AI Engineering Intern',
  // Data & AI
  'Data Scientist', 'Data Analyst', 'ML Engineer', 'AI Engineer',
  // Product & Design
  'Product Manager', 'Product Designer', 'UX/UI Designer',
  // Sales & BD
  'Sales Manager', 'Sales Intern', 'Account Executive', 'Account Manager',
  'Business Development', 'Enterprise Sales',
  // Leadership & Founding
  'Founders Associate', 'Chief of Staff', 'Head of …', 'Director',
  'Operations Manager',
  // Marketing & Growth
  'Marketing Manager', 'Brand Manager', 'Growth Manager',
  'Social Media Manager', 'Content Creator', 'Influencer Marketing',
  // People & HR
  'People & Culture', 'Talent Acquisition', 'Recruiter',
  // Finance & Ops
  'Finance Manager', 'Controlling', 'Accountant',
  // Customer & Success
  'Customer Success Manager', 'Support Manager',
  // Consulting
  'Consultant',
  // Editorial & Creative
  'Redakteur:in', 'Texter:in / Copywriter',
  // Legal
  'Jurist:in', 'Rechtsanwält:in', 'Legal Counsel', 'Notar:in',
  // Health (selective)
  'Psycholog:in',
  // Early-career & Categories
  'Working Student', 'Werkstudent:innen', 'Praktikum', 'Internship',
  'Thesis Project',
  'Office', 'Assistenz', 'Technik', 'Compliance', 'Verkauf', 'Küche',
];

export const SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

export const FUNDING_OPTIONS = ['Non-Profit', 'Öffentlich gefördert', 'Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C+', 'Bootstrapped', 'Public'];
